/**
 * The account's monthly credits. Refreshes itself after anything that spends
 * credits (see CREDITS_CHANGED in lib/api).
 */
import { useEffect, useState } from "react";
import { billingApi, CREDITS_CHANGED, planName, type Credits } from "@/lib/api";
import { DuotoneIcon, Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

export function useCredits() {
  const [credits, setCredits] = useState<Credits | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => billingApi.credits().then((r) => { if (alive && "data" in r && r.data) setCredits(r.data); });
    load();
    window.addEventListener(CREDITS_CHANGED, load);
    return () => { alive = false; window.removeEventListener(CREDITS_CHANGED, load); };
  }, []);
  return credits;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

function tone(c: Credits) {
  const left = c.remaining / Math.max(1, c.allowance);
  return c.remaining === 0 ? "danger" : left <= 0.15 ? "warn" : "ok";
}

/** Sidebar card: plan, credits left, refill date, and usage progress. */
export function CreditsCard({ className }: { className?: string }) {
  const c = useCredits();
  if (!c) return null;
  const t = tone(c);
  return (
    <div className={cn("rounded-lg bg-muted/60 px-3 py-2.5 space-y-2", className)}>
      <div className="flex items-center gap-1.5">
        <DuotoneIcon name="flashlight" size={12} className={cn("shrink-0", t === "danger" ? "text-destructive" : "text-amber-400")} />
        <p className="font-mono text-[11px] uppercase tracking-widest font-semibold">{planName(c.plan)}</p>
        <span className={cn(
          "ml-auto font-mono text-[11px] tabular-nums",
          t === "danger" ? "text-destructive" : t === "warn" ? "text-amber-400" : "text-muted-foreground",
        )}>
          {c.remaining.toLocaleString()} / {c.allowance.toLocaleString()}
        </span>
      </div>
      <Bar credits={c} />
      <p className="font-sans text-[11px] text-muted-foreground">
        {c.remaining === 0 ? "Out of credits" : "Credits"} · refill {fmtDate(c.resetsAt)}
      </p>
    </div>
  );
}

/** Billing tab: balance, refill date and what each action costs. */
export function CreditsPanel({ credits: c, costs }: { credits: Credits; costs: { chat: number; mira: number; discovery: number; analysis: number } }) {
  const rows: [string, number][] = [
    ["Chat message", costs.chat],
    ["Mira question or briefing", costs.mira],
    ["Competitor discovery", costs.discovery],
    ["Deep competitor analysis", costs.analysis],
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-3xl font-bold leading-none tabular-nums">
            {c.remaining.toLocaleString()}
            <span className="ml-1 font-sans text-xs font-normal text-muted-foreground">of {c.allowance.toLocaleString()} credits left</span>
          </p>
          <p className="mt-1.5 font-sans text-xs text-muted-foreground">
            {c.used.toLocaleString()} used this month · refills on {fmtDate(c.resetsAt)}
          </p>
        </div>
      </div>
      <Bar credits={c} />
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-4">
        {rows.map(([label, cost]) => (
          <div key={label} className="flex items-baseline justify-between gap-2 sm:block">
            <p className="font-sans text-[11px] text-muted-foreground">{label}</p>
            <p className="font-mono text-xs tabular-nums">{cost} {cost === 1 ? "credit" : "credits"}</p>
          </div>
        ))}
      </div>
      <p className="flex items-center gap-1.5 font-sans text-[11px] text-muted-foreground">
        <Icon name="information-circle" size={11} />
        Monitoring and alerts don't use credits.
      </p>
    </div>
  );
}

function Bar({ credits: c }: { credits: Credits }) {
  const t = tone(c);
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-foreground/10">
      <div
        className={cn("h-full rounded-full transition-all", t === "danger" ? "bg-destructive" : t === "warn" ? "bg-amber-400" : "bg-foreground")}
        style={{ width: `${Math.min(100, (c.remaining / Math.max(1, c.allowance)) * 100)}%` }}
      />
    </div>
  );
}
